import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { smoothScroll } from "../lib/utils";
import { 
  CheckCircle2, 
  Phone, 
  ArrowRight,
  ClipboardList,
  Truck,
  HeartHandshake,
  Utensils,
  Wind,
  Fan,
  Moon
} from "lucide-react";
import { Button } from "../components/ui/button";
import { ServiceCard } from "../components/ui/service-card";

export function Home() {
  const location = useLocation();

  useEffect(() => {
    if (location.state && (location.state as any).scrollTo) {
      setTimeout(() => {
        smoothScroll(`#${(location.state as any).scrollTo}`);
      }, 100);
    }
  }, [location]);

  return (
    <div className="flex flex-col gap-16 pb-16">
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 w-full h-full">
          <video 
            src="/video/Respiro.mp4" 
            className="w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
          />
        </div>

        <div className="container relative z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight">
              BENESSERE E VITALITÀ INIZIA DAL RESPIRO
            </h1>
            <p className="text-lg md:text-xl text-gray-100 max-w-xl">
              Assistenza domiciliare personalizzata per Ossigenoterapia, Ventiloterapia, 
              Disturbi del Sonno e Nutrizione.<br />Cure professionali nel comfort di casa tua.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button size="lg" className="bg-brand-secondary hover:bg-brand-secondary/90 border-none text-black" asChild>
                <Link to="/contatti">
                  Contattaci Ora <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="text-white border-white hover:bg-white hover:text-brand-primary" asChild>
                <a 
                  href="#servizi" 
                  onClick={(e) => {
                    e.preventDefault();
                    smoothScroll("#servizi");
                  }}
                >
                  Scopri i Servizi
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Servizi Cards */}
      <section className="container" id="servizi">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-text-title mb-6">I Nostri Servizi</h2>
          <p className="text-xl md:text-2xl text-text-body max-w-3xl mx-auto leading-relaxed">
            Soluzioni complete per la salute respiratoria e nutrizionale, direttamente a casa tua.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <ServiceCard
            title="Ossigenoterapia"
            subtitle="Respiro e Vitalità"
            description="Terapie con ossigeno liquido e gassoso per la tua insufficienza respiratoria."
            href="/servizi/ossigenoterapia"
            imageSrc="/images/services/ossigenoterapia.webp"
            imageAlt="Donna serena che indossa una maschera per ossigenoterapia in un contesto di benessere"
          />
          <ServiceCard
            title="Ventiloterapia"
            subtitle="Supporto Avanzato"
            description="Tecnologie all'avanguardia per il supporto ventilatorio domiciliare."
            href="/servizi/ventiloterapia"
            imageSrc="/images/services/ventiloterapia.webp"
            imageAlt="Dettaglio di attrezzatura medica avanzata per la ventilazione assistita"
          />
          <ServiceCard
            title="Disturbi del Sonno"
            subtitle="Riposo Rigenerante"
            description="Diagnosi e trattamento delle apnee notturne per ritrovare il giusto riposo."
            href="/servizi/sonno"
            imageSrc="/images/services/sonno.jpg"
            imageAlt="Uomo che dorme serenamente, rappresentazione del riposo rigenerante"
          />
          <ServiceCard
            title="Nutrizione"
            subtitle="Piani dietetici personalizzati"
            description="Nutrizione artificiale e supporto specialistico direttamente a casa tua."
            href="/servizi/nutrizione"
            imageSrc="/images/services/nutrizione.webp"
            imageAlt="Operatore sanitario che assiste con cura una persona anziana"
          />
        </div>
      </section>

      {/* Come Funziona */}
      <section className="bg-brand-neutral py-16">
        <div className="container">
          <h2 className="text-3xl font-heading font-bold text-text-title text-center mb-12">Come Funziona</h2>
          <div className="grid md:grid-cols-3 gap-8 relative">
            {[
              {
                step: "01",
                title: "Valutazione",
                desc: "Analizziamo le tue necessità mediche e definiamo il piano di assistenza ideale.",
                icon: ClipboardList
              },
              {
                step: "02",
                title: "Consegna e Avvio",
                desc: "Consegniamo i dispositivi a casa tua e ti formiamo sul loro utilizzo corretto.",
                icon: Truck
              },
              {
                step: "03",
                title: "Assistenza Continua",
                desc: "Monitoriamo la terapia e forniamo supporto tecnico e sanitario 24/7.",
                icon: HeartHandshake
              }
            ].map((item, index) => (
              <div key={item.step} className="relative flex flex-col items-center text-center">
                {index < 2 && (
                  <div className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gray-200 -z-10" />
                )}
                <div className="w-16 h-16 bg-white rounded-full shadow-md flex items-center justify-center mb-6 border-2 border-brand-primary/20 text-brand-primary">
                  <item.icon className="h-8 w-8" />
                </div>
                <span className="text-4xl font-bold text-gray-100 absolute -top-4 -right-4 -z-10 select-none">
                  {item.step}
                </span>
                <h3 className="text-xl font-bold text-text-title mb-3">{item.title}</h3>
                <p className="text-text-body">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Perché Puglia Life */}
      <section className="container">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src="/images/services/teams3.png"
              alt="Team multidisciplinare di Puglia Life" 
              className="rounded-2xl shadow-lg w-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="space-y-6">
            <h2 className="text-3xl font-heading font-bold text-text-title">Perché Scegliere Puglia Life</h2>
            <p className="text-text-body text-lg">
              Da oltre 20 anni ci prendiamo cura dei pazienti pugliesi con passione, professionalità e tecnologie all'avanguardia.
            </p>
            <ul className="space-y-4">
              {[
                "Team multidisciplinare di esperti sanitari",
                "Reperibilità e assistenza tecnica 24/7",
                "Dispositivi medici di ultima generazione",
                "Convenzionati con le principali ASL",
                "Approccio umano ed empatico al paziente"
              ].map((point) => (
                <li key={point} className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-brand-primary shrink-0" />
                  <span className="text-text-body font-medium">{point}</span>
                </li>
              ))}
            </ul>
            <Button variant="primary" className="mt-4" asChild>
              <Link to="/chi-siamo">Conosci il nostro Team</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-brand-primary py-16 text-white">
        <div className="container text-center space-y-6">
          <h2 className="text-3xl font-heading font-bold">Hai bisogno di assistenza immediata?</h2>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto">
            Il nostro team è pronto ad ascoltare le tue esigenze e trovare la soluzione migliore per te o per i tuoi cari.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
            <Button size="lg" variant="secondary" className="gap-2" asChild>
              <Link to="/contatti">
                <Phone className="h-5 w-5" /> Contattaci Subito
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-brand-primary" asChild>
              <Link to="/faq">Leggi le FAQ</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
